import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { disableAnalytics, enableAnalytics, getAnalyticsConsent } from "../analytics/ga4";

export const AnalyticsConsent = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(() => getAnalyticsConsent() === null);
  const [view, setView] = useState("banner");
  const [analyticsSelected, setAnalyticsSelected] = useState(false);

  useEffect(() => {
    const openSettings = () => {
      setAnalyticsSelected(getAnalyticsConsent() === "granted");
      setView("settings");
      setVisible(true);
    };
    window.addEventListener("analytics-settings", openSettings);
    return () => window.removeEventListener("analytics-settings", openSettings);
  }, []);

  if (!visible) return null;

  const closeWithoutConsent = () => {
    if (getAnalyticsConsent() === null) disableAnalytics();
    setView("banner");
    setVisible(false);
  };

  const accept = async () => {
    setView("banner");
    setVisible(false);
    try {
      await enableAnalytics();
    } catch {
      // The consent choice remains stored; analytics retries on the next page view.
    }
  };

  const saveSelection = async () => {
    if (analyticsSelected) {
      try {
        await enableAnalytics();
      } catch {
        // The consent choice remains stored; analytics retries on the next page view.
      }
    } else {
      disableAnalytics();
    }
    setView("banner");
    setVisible(false);
  };

  const openSettings = () => {
    setAnalyticsSelected(getAnalyticsConsent() === "granted");
    setView("settings");
  };

  return (
    <div
      data-testid="analytics-consent-banner"
      role="dialog"
      aria-modal="false"
      aria-labelledby="analytics-consent-title"
      className="fixed inset-x-0 bottom-0 z-[110] bg-[#032C37] text-white"
    >
      {view === "banner" ? (
        <div className="mx-auto flex max-w-[1600px] flex-col gap-4 px-6 py-4 sm:px-12 md:flex-row md:items-center md:justify-between md:px-16">
          <p id="analytics-consent-title" className="text-[13px] font-light leading-relaxed text-white/85 md:max-w-[58%]">
            {t.consent.text}{" "}
            <Link
              to="/datenschutz"
              data-testid="analytics-privacy-link"
              className="underline decoration-white/40 underline-offset-4 transition-colors hover:decoration-white"
            >
              {t.consent.privacy}
            </Link>
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="analytics-settings-button"
              onClick={openSettings}
              className="border border-white/60 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:border-white"
            >
              {t.consent.settings}
            </button>
            <button
              type="button"
              data-testid="analytics-accept-button"
              onClick={accept}
              className="bg-white px-4 py-2 text-[11px] font-medium uppercase tracking-[0.18em] text-[#032C37] transition-colors duration-300 hover:bg-mist"
            >
              {t.consent.accept}
            </button>
            <button
              type="button"
              data-testid="analytics-close-button"
              onClick={closeWithoutConsent}
              aria-label={t.consent.close}
              className="flex h-9 w-9 items-center justify-center text-white transition-colors hover:text-white/70"
            >
              <X size={18} strokeWidth={1.5} aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : (
        <div className="mx-auto max-w-[1600px] px-6 py-5 sm:px-12 md:px-16">
          <div className="flex items-center justify-between gap-6">
            <h2 id="analytics-consent-title" className="font-head text-xl leading-tight text-white sm:text-2xl">
              {t.consent.settingsTitle}
            </h2>
            <button
              type="button"
              data-testid="analytics-close-button"
              onClick={closeWithoutConsent}
              aria-label={t.consent.close}
              className="flex h-9 w-9 items-center justify-center text-white transition-colors hover:text-white/70"
            >
              <X size={18} strokeWidth={1.5} aria-hidden="true" />
            </button>
          </div>
          <div className="mt-5 grid gap-5 md:grid-cols-[1fr_1fr_auto] md:items-center">
            <div className="border-t border-white/15 pt-4">
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm font-light text-white/85">{t.consent.necessary}</span>
                <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/60">
                  {t.consent.alwaysActive}
                </span>
              </div>
            </div>
            <div className="border-t border-white/15 pt-4">
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm font-light text-white/85">{t.consent.analytics}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={analyticsSelected}
                  data-testid="analytics-category-toggle"
                  onClick={() => setAnalyticsSelected(!analyticsSelected)}
                  className={`relative h-6 w-11 rounded-full border ${
                    analyticsSelected ? "border-white bg-white" : "border-white/40 bg-transparent"
                  }`}
                >
                  <span
                    className={`absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full ${
                      analyticsSelected ? "left-0 translate-x-[26px] bg-[#032C37]" : "left-0 translate-x-[4px] bg-white"
                    }`}
                  />
                </button>
              </div>
            </div>
            <button
              type="button"
              data-testid="analytics-save-button"
              onClick={saveSelection}
              className="bg-white px-4 py-2 text-[11px] font-medium uppercase tracking-[0.18em] text-[#032C37] transition-colors duration-300 hover:bg-mist"
            >
              {t.consent.save}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
